import React, { useState } from "react";

import { animated, useSpring, useTransition } from "@react-spring/web";
import {
  Box,
  Checkbox,
  Typography,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  MenuItem,
  Button,
} from "@mui/material";
import {
  AutoStories,
  Spellcheck,
  TextFields,
  ElectricBolt,
  Calculate,
  Extension,
  Language,
} from "@mui/icons-material";
import GradeSelector, { gradeInstructions } from "./GradeSelector";
import ThemeSelector from "./HpThemeSelector";

import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const QuestionTypeSelector = ({
  type,
  selectedQuestionTypes,
  handleQuestionTypeChange,
  selectedThemes,
  handleThemeChange,
  questionCounts,
  handleQuestionCountChange,
  theme,
  user,
  showSnackbar,
}) => {
  const icons = {
    ORD: <Spellcheck />,
    LÄS: <AutoStories />,
    MEK: <TextFields />,
    ELF: <Language />,
    XYZ: <Calculate />,
    DTK: <Extension />,
  };

  const questionTypeDescriptions = {
    ORD: "Testar förmågan att förstå och använda ord korrekt.",
    LÄS: "Analysera texter och svara på frågor om innehållet.",
    MEK: "Testa din språkliga förmåga och grammatik.",
    ELF: "Testa din förståelse av engelska texter.",
    XYZ: "Lös ekvationer och matematiska uppgifter.",
    DTK: "Diagram, tabeller och kvantitativa samband - Analysera numerisk information.",
  };

  const questionTypeTitles = {
    ORD: "Ordförståelse",
    LÄS: "Läsförståelse",
    MEK: "Meningsbyggnad",
    ELF: "Engelsk läsförståelse",
    XYZ: "Matematik",
    DTK: "Diagram, tabeller och kvantitativa samband",
  };

  const isSelected = selectedQuestionTypes.includes(type);
  const isDark = theme.palette.mode === "dark";
  const [grade, setGrade] = useState("åk8");
  // Animation for card flip
  const { transform, opacity } = useSpring({
    opacity: isSelected ? 1 : 0,
    transform: `perspective(1200px) rotateY(${isSelected ? 180 : 0}deg)`,
    config: { mass: 5, tension: 500, friction: 80 },
    // Add this to ensure the animation completes properly
    from: {
      opacity: isSelected ? 0 : 1,
      transform: `perspective(1200px) rotateY(${isSelected ? 0 : 180}deg)`,
    },
  });

  const getTooltipTitle = (type) => {
    if (type === "DTK") return "Kommer snart!";
    if (type === "XYZ")
      return (
        <React.Fragment>
          <em>{"Just nu stödjer vi ekvationer, mer kommer snart."}</em>
        </React.Fragment>
      );
    return questionTypeDescriptions[type];
  };

  const handleCardClick = () => {
    if (type === "DTK") return; // Disabled card

    handleQuestionTypeChange({
      target: { name: type, checked: !isSelected },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",

        height: "250px", // Fixed height for both states
        width: "100%",
        position: "relative",
        perspective: "1200px", // Slightly increased perspective
        transformStyle: "preserve-3d",
      }}
    >
      {/* Front of the card - update these properties */}
      <animated.div
        style={{
          opacity: opacity.to((o) => 1 - o),
          transform,
          position: "absolute",
          width: "100%",
          height: "100%",
          backfaceVisibility: "hidden",
          zIndex: isSelected ? 0 : 1,
          transformStyle: "preserve-3d", // Add this
        }}
      >
        <Box
          sx={{
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(139, 92, 246, 0.05)",
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: type === "DTK" ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            width: "100%",
            height: "100%",
            opacity: type === "DTK" ? 0.5 : 1,
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(139, 92, 246, 0.1)",
              transform: "translateY(-2px)",
            },
          }}
          onClick={handleCardClick}
        >
          <Box
            className="icon"
            sx={{
              color: theme.palette.primary.main,
              mb: 1,
              "& .MuiSvgIcon-root": {
                fontSize: "40px",
                color: isDark ? "rgba(255, 255, 255, 0.7)" : "#8b5cf6",
              },
            }}
          >
            {icons[type]}
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              mb: 1,
              color: isDark ? "rgba(255, 255, 255, 0.7)" : "#4a5568",
              fontWeight: "600",
              textAlign: "center",
            }}
          >
            {questionTypeTitles[type]}
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              mb: 1,
              color: isDark ? "rgba(255, 255, 255, 0.5)" : "#718096",
              textAlign: "center",
              maxWidth: "180px",

              fontSize: "0.8rem",
            }}
          >
            {questionTypeDescriptions[type]}
          </Typography>
          <Checkbox
            checked={false}
            onChange={() => {}}
            icon={<RadioButtonUncheckedIcon sx={{ fontSize: "35px" }} />}
            sx={{
              "&.MuiCheckbox-root": {
                color: theme.palette.grey[400],
              },
              "& .MuiSvgIcon-root": {
                fontSize: "30px",
              },
            }}
          />
        </Box>
      </animated.div>

      {/* Back of the card (configuration) */}
      <animated.div
        style={{
          opacity,
          transform: transform.to((t) => `${t} rotateY(180deg)`),
          position: "absolute",
          width: "100%",
          height: "100%", // Make sure this matches
          backfaceVisibility: "hidden",
          zIndex: isSelected ? 1 : 0,
          transformStyle: "preserve-3d", // Add this
          overflow: "auto", // Allow scrolling if content is too large
        }}
      >
        {/* Back card content - might need height adjustments */}
        <Box
          sx={{
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.08)"
              : "rgb(81 181 83 / 8%)",
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%", // Make sure this fills the container
            border: `1px solid rgb(81 181 84 / 16%)`,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            overflowY: "auto", // Allow content to scroll if needed
          }}
        >
          {/* Header with selected indicator */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                sx={{
                  mr: 1,
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "#8b5cf6",
                }}
              >
                {icons[type]}
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "#4a5568",
                }}
              >
                {questionTypeTitles[type]}
              </Typography>
            </Box>
            <CheckCircleIcon
              sx={{
                color: "rgb(81 181 84)",
                fontSize: "28px",
              }}
            />
          </Box>

          {/* Theme selection */}

          <ThemeSelector
            type={type}
            selectedTheme={selectedThemes[type]}
            handleThemeChange={handleThemeChange}
            isDark={theme.palette.mode === "dark"}
            showSnackbar={showSnackbar}
          />

          {/* Question count selection */}
          <FormControl sx={{ mb: 0.5, mt: 1 }} fullWidth>
            <InputLabel id={`${type}-count-label`} sx={{ fontWeight: "500" }}>
              Antal frågor
            </InputLabel>
            <Select
              labelId={`${type}-count-label`}
              value={questionCounts[type]}
              onChange={(e) => handleQuestionCountChange(type, e.target.value)}
              label={`Frågor för ${type}`}
              size="small"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1c262f" : "white",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                fontSize: "0.9rem", // This will style the selected item text
                fontWeight: "500",
              }}
              MenuProps={{
                sx: {
                  "& .MuiMenuItem-root": {
                    fontSize: "0.9rem",
                    fontWeight: "500",
                  },
                },
              }}
            >
              {type === "LÄS" && <MenuItem value={1}>1</MenuItem>}
              {type === "LÄS" && <MenuItem value={2}>2</MenuItem>}
              {type === "DTK" &&
                [1, 2, 3, 4, 5, 10].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              {/* XYZ type - restrict to 1-3 for regular users, 4-5 for premium */}
              {type === "XYZ" &&
                [1, 2, 3].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              {type === "XYZ" && (
                <>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={4} disabled={!user.isPremium}>
                        4
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={5} disabled={!user.isPremium}>
                        5
                      </MenuItem>
                    </span>
                  </Tooltip>
                </>
              )}
              {/* MEK type - restrict to 1-3 for regular users, 4-5 for premium */}
              {type === "MEK" && <MenuItem value={1}>1</MenuItem>}
              {type === "MEK" && <MenuItem value={2}>2</MenuItem>}
              {type === "MEK" && <MenuItem value={3}>3</MenuItem>}
              {type === "MEK" && (
                <>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={4} disabled={!user.isPremium}>
                        4
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={5} disabled={!user.isPremium}>
                        5
                      </MenuItem>
                    </span>
                  </Tooltip>
                </>
              )}
              {type === "ELF" && <MenuItem value={1}>1</MenuItem>}
              {type === "ELF" && <MenuItem value={2}>2</MenuItem>}
              {type === "ORD" && <MenuItem value={2}>2</MenuItem>}
              {type === "ORD" && <MenuItem value={5}>5</MenuItem>}
              {/* Premium options for ORD type */}
              {type === "ORD" && (
                <Tooltip
                  title={
                    !user.isPremium
                      ? "Uppgradera för att kunna välja fler frågor"
                      : ""
                  }
                >
                  <span>
                    <MenuItem value={10} disabled={!user.isPremium}>
                      10
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
              {type === "ORD" && (
                <Tooltip
                  title={
                    !user.isPremium
                      ? "Uppgradera för att kunna välja fler frågor"
                      : ""
                  }
                >
                  <span>
                    <MenuItem value={15} disabled={!user.isPremium}>
                      15
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
              {type === "ORD" && (
                <Tooltip
                  title={
                    !user.isPremium
                      ? "Uppgradera för att kunna välja fler frågor"
                      : ""
                  }
                >
                  <span>
                    <MenuItem value={20} disabled={!user.isPremium}>
                      20
                    </MenuItem>
                  </span>
                </Tooltip>
              )}
              {/* Premium options for LÄS, ELF types */}
              {["LÄS", "ELF"].includes(type) && (
                <>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={5} disabled={!user.isPremium}>
                        5
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !user.isPremium
                        ? "Uppgradera för att kunna välja fler frågor"
                        : ""
                    }
                  >
                    <span>
                      <MenuItem value={10} disabled={!user.isPremium}>
                        10
                      </MenuItem>
                    </span>
                  </Tooltip>
                </>
              )}
              {/* Common premium options for all types */}
              <Tooltip
                title={
                  !user.isPremium
                    ? "Uppgradera för att kunna välja fler frågor"
                    : ""
                }
              >
                <span>
                  <MenuItem value={15} disabled={!user.isPremium}>
                    15
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  !user.isPremium
                    ? "Uppgradera för att kunna välja fler frågor"
                    : ""
                }
              >
                <span>
                  <MenuItem value={20} disabled={!user.isPremium}>
                    20
                  </MenuItem>
                </span>
              </Tooltip>
            </Select>
          </FormControl>

          {/* Button to flip back */}
          {/* Button to flip back */}
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Tooltip title="Välj bort frågetyp">
              <IconButton onClick={handleCardClick} size="large">
                <HighlightOffIcon sx={{ fontSize: "2rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </animated.div>
    </Box>
  );
};

export default QuestionTypeSelector;
